import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Phone, MessageCircle, Sparkles } from 'lucide-react';

const Hero = () => {
  return (
    <section id="home" className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden min-h-screen">
      {/* Gradient Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500">
        <div className="absolute inset-0 bg-black/30"></div>
      </div>
      
      {/* Background Decorations */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full opacity-20 animate-float z-10" style={{ backgroundColor: 'var(--primary-yellow)' }}></div>
      <div className="absolute top-40 right-20 w-24 h-24 rounded-full opacity-20 animate-float z-10" style={{ backgroundColor: 'var(--primary-blue)', animationDelay: '1s' }}></div>
      <div className="absolute bottom-20 left-1/4 w-20 h-20 rounded-full opacity-20 animate-float z-10" style={{ backgroundColor: 'var(--primary-red)', animationDelay: '2s' }}></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-6 z-10">
            {/* Now Enrolling Badge */}
            <div className="inline-flex items-center space-x-2 px-6 py-3 rounded-full animate-pulse-gentle" style={{ backgroundColor: 'var(--primary-yellow)' }}>
              <Sparkles className="h-5 w-5" style={{ color: 'var(--primary-red)' }} />
              <span className="font-bold text-lg heading-playful" style={{ color: 'var(--text-primary)' }}>
                Now Enrolling for Session 2026–27!
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold heading-playful leading-tight">
              <span style={{ color: 'var(--primary-blue)' }}>Welcome to</span>
              <br />
              <span style={{ color: 'var(--primary-red)' }}>HR KinderNova</span>
            </h1>

            {/* Tagline */}
            <p className="text-xl md:text-2xl font-medium" style={{ color: 'var(--text-secondary)' }}>
              Where Little Stars Shine Bright! ⭐
            </p>

            <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
              Play-Way & Activity-Based Learning for Playgroup to 1st Class
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button
                onClick={() => document.querySelector('#admissions').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl"
                style={{ backgroundColor: 'var(--primary-blue)', color: 'white' }}
              >
                Enquire Now
              </Button>
              <a href="https://wa.me/919467954126" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg font-semibold border-2 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                  style={{ borderColor: 'var(--primary-green)', color: 'var(--primary-green)' }}
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>WhatsApp Us</span>
                </Button>
              </a>
            </div>

            {/* Contact Info */}
            <div className="flex items-center justify-center lg:justify-start space-x-2 pt-4">
              <Phone className="h-5 w-5" style={{ color: 'var(--primary-green)' }} />
              <a href="tel:9467954126" className="text-lg font-semibold" style={{ color: 'var(--primary-green)' }}>
                9467954126
              </a>
            </div>
          </div>

          {/* Right Image - School Entrance */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_41ad9bc8-daac-4809-894b-90c60487a24b/artifacts/ywk38vni_image.png"
                alt="HR Hindu High School - HR KinderNova Entrance"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Floating Cards */}
            <div className="absolute -bottom-6 -left-6 p-4 rounded-2xl shadow-xl animate-float" style={{ backgroundColor: 'var(--primary-yellow)' }}>
              <p className="font-bold text-2xl heading-playful" style={{ color: 'var(--text-primary)' }}>Playgroup to 1st Class</p>
            </div>
            <div className="absolute -top-6 -right-6 p-4 rounded-2xl shadow-xl animate-float" style={{ backgroundColor: 'var(--primary-green)', animationDelay: '1s' }}>
              <p className="font-bold text-xl heading-playful text-white">AC Classrooms</p>
            </div>
          </div>
        </div>

        {/* Key Highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { title: 'Safe Campus', color: 'var(--primary-blue)' },
            { title: 'CCTV Security', color: 'var(--primary-green)' },
            { title: 'Experienced Teachers', color: 'var(--primary-red)' },
            { title: 'Activity-Based Learning', color: 'var(--primary-orange)' },
          ].map((item, index) => (
            <div
              key={index}
              className="text-center p-6 rounded-2xl shadow-lg"
              style={{ backgroundColor: 'white' }}
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: item.color }}>
                <span className="text-2xl">✓</span>
              </div>
              <p className="font-semibold heading-playful" style={{ color: 'var(--text-primary)' }}>
                {item.title}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;